
import React, { useEffect, useMemo } from 'react';
import type { Department, StaffMember, Shift, Schedule, AdminUser, AssignmentStatus, WeeklyMetrics } from '../../types';
import { getWeekDates, formatDayAndDate, formatDateRange, formatDateYYYYMMDD } from '../../utils/dateUtils';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

export interface PrintData {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weekStartDate: Date; 
    weeklyMetrics?: WeeklyMetrics;
}

interface PrintPageProps extends PrintData {
    onDone: () => void;
}


const PrintPage: React.FC<PrintPageProps> = ({ departments, people, shifts, schedule, weekStartDate, weeklyMetrics = {}, onDone }) => {

    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        // Prepare body for printing by removing the default gray background
        document.body.style.cssText = 'background-color: white;';
        
        // A short timeout allows the component to fully render before the print dialog blocks the main thread.
        const timer = setTimeout(() => {
            window.print();
            // This is called after the user interacts with (prints or cancels) the print dialog.
            onDone();
        }, 250); 

        return () => {
            clearTimeout(timer);
            // Restore original body style when the component unmounts
            document.body.style.cssText = originalBodyStyle;
        };
    }, [onDone]);

    const weekDates = useMemo(() => getWeekDates(weekStartDate), [weekStartDate]);
    const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));

    const staffWeeklyHours = useMemo(() => {
        const hours: { [staffId: string]: number } = {};
        for (const person of people) {
            let personHours = 0;
            for (const date of weekDates) {
                const dateString = formatDateYYYYMMDD(date);
                const entry = schedule[person.id]?.[dateString];
                if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                    const shift = shiftsMap.get(entry.shiftId);
                    if (shift) {
                        const start = new Date(`1970-01-01T${shift.startTime}:00`);
                        const end = new Date(`1970-01-01T${shift.endTime}:00`);
                        if (end < start) {
                            end.setDate(end.getDate() + 1);
                        }
                        personHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }
                }
            }
            hours[person.id] = personHours;
        }
        return hours;
    }, [schedule, people, shiftsMap, weekDates]);

    const dailyDepartmentHours = useMemo(() => {
        const hours: { [date: string]: { [depId: string]: number } } = {};
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            hours[dateString] = {};
            departments.forEach(dep => hours[dateString][dep.id] = 0);
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED' && entry.shiftId) {
                        const shift = shiftsMap.get(entry.shiftId);
                        if (shift) {
                            const start = new Date(`1970-01-01T${shift.startTime}:00`);
                            const end = new Date(`1970-01-01T${shift.endTime}:00`);
                            if (end < start) end.setDate(end.getDate() + 1);
                            hours[dateString][person.departmentId!] += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                        }
                    }
                });
            }
        });
        return hours;
    }, [schedule, people, departments, weekDates, shiftsMap]);

    const departmentWeeklyHours = useMemo(() => {
        const hours: { [depId: string]: number } = {};
        departments.forEach(dep => hours[dep.id] = 0);
        people.forEach(person => {
            if (person.departmentId) {
                hours[person.departmentId] += staffWeeklyHours[person.id] || 0;
            }
        });
        return hours;
    }, [departments, people, staffWeeklyHours]);

    const departmentStaffMap = new Map<string, SchedulablePerson[]>();
    departments.forEach(dep => departmentStaffMap.set(dep.id, []));
    people.forEach(person => {
        if (person.departmentId && departmentStaffMap.has(person.departmentId)) {
            departmentStaffMap.get(person.departmentId)?.push(person);
        }
    });

    const statusLabels: { [key in AssignmentStatus]?: string } = {
        OFF: 'OFF',
        SICK: 'SICK',
        VACATION: 'VACATION',
        ON_CALL: 'ON CALL',
        UNASSIGNED: '-',
    };

    const cellStyle: React.CSSProperties = {
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '10px',
        paddingRight: '10px',
        lineHeight: '1.4',
        verticalAlign: 'top',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        border: '1px solid #d1d5db'
    };

    const headerCellStyle: React.CSSProperties = {
        ...cellStyle,
        backgroundColor: '#f3f4f6',
        fontWeight: 'bold',
        color: '#374151'
    };

    return (
         <div className="p-4 sm:p-6 lg:p-8">
            <style type="text/css" media="print">
            {`
                @page { size: landscape; margin: 12mm; }
                body { -webkit-print-color-adjust: exact; color-adjust: exact; }
                .break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
            `}
            </style>
            <main className="bg-white">
                <div className="flex items-center justify-between pb-3 mb-4" style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight text-gray-900">Weekly Schedule Report</h1>
                        <p className="text-xs text-gray-500 font-medium">Scheduler Pro Platform</p>
                    </div>
                    <div className="text-right">
                        <h2 className="text-sm font-bold text-gray-800">{formatDateRange(weekStartDate)}</h2>
                        <p className="text-[10px] text-gray-500 font-medium mt-0.5">Generated On: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300" style={{ tableLayout: 'auto', width: '100%' }}>
                        <thead>
                            <tr className="bg-gray-100">
                                <th scope="col" style={{ ...headerCellStyle, width: '16%' }} className="text-left text-sm font-semibold text-gray-700">
                                    Staff Member
                                </th>
                                {weekDates.map((date) => {
                                    const { day, date: dateNum } = formatDayAndDate(date);
                                    return (
                                        <th key={date.toISOString()} scope="col" style={{ ...headerCellStyle, width: '11%' }} className="text-center text-sm font-semibold text-gray-700">
                                            {day} <span className="font-normal text-gray-600">{dateNum}</span>
                                        </th>
                                    );
                                })}
                                <th scope="col" style={{ ...headerCellStyle, width: '7%' }} className="text-center text-sm font-semibold text-gray-700">
                                    Hours
                                </th>
                            </tr>
                            <tr style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                                <td style={{ ...cellStyle, fontWeight: 'bold', backgroundColor: '#fef3c7', color: '#92400e', borderBottom: '1.5px solid #cbd5e1' }} className="text-left text-xs uppercase tracking-wider">
                                    Occupancy
                                </td>
                                {weekDates.map((date) => {
                                    const dateString = formatDateYYYYMMDD(date);
                                    const occ = weeklyMetrics[dateString]?.occupancy || 0;
                                    return (
                                        <td key={`${date.toISOString()}-print-hdr-occ`} style={{ ...cellStyle, backgroundColor: '#fffbeb', fontWeight: 'bold', color: '#b45309', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-sm">
                                            {occ}%
                                        </td>
                                    );
                                })}
                                <td style={{ ...cellStyle, backgroundColor: '#fef3c7', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-sm"></td>
                            </tr>
                        </thead>
                        <tbody>
                            {departments.map(dep => (
                                <React.Fragment key={dep.id}>
                                    <tr className="bg-gray-200">
                                        <td style={{ ...cellStyle, backgroundColor: '#e5e7eb', fontWeight: 'bold' }} className="text-sm text-gray-800">
                                            {dep.name}
                                        </td>
                                        {weekDates.map(date => {
                                            const dateString = formatDateYYYYMMDD(date);
                                            return (
                                                <td key={dateString} style={{ ...cellStyle, backgroundColor: '#e5e7eb', fontWeight: 'bold' }} className="text-center text-[10px] text-gray-700">
                                                    {dailyDepartmentHours[dateString][dep.id].toFixed(1)}h
                                                </td>
                                            );
                                        })}
                                        <td style={{ ...cellStyle, backgroundColor: '#e5e7eb', fontWeight: 'bold' }} className="text-center text-sm text-gray-800">
                                            {departmentWeeklyHours[dep.id].toFixed(1)}
                                        </td>
                                    </tr>
                                    {departmentStaffMap.get(dep.id)?.map(person => (
                                        <tr key={person.id} className="even:bg-gray-50 break-inside-avoid">
                                            <td style={cellStyle} className="font-medium text-gray-800">
                                                <div className="font-bold text-gray-950">{person.name}</div>
                                                <div className="text-[10px] text-gray-500 font-normal leading-tight mt-1">{person.role}</div>
                                            </td>
                                            {weekDates.map(date => {
                                                const dateString = formatDateYYYYMMDD(date);
                                                const entry = schedule[person.id]?.[dateString];
                                                let content: React.ReactNode = <span className="text-gray-400">-</span>;
                                                let bgColor = 'transparent';

                                                if (entry) {
                                                    if (entry.status === 'ASSIGNED' && entry.shiftId) {
                                                        const shift = shiftsMap.get(entry.shiftId);
                                                        if (shift) {
                                                            content = (
                                                                <div className="font-semibold text-xs leading-tight">
                                                                    <div className="font-bold text-gray-900">{shift.name}</div>
                                                                    <div className="text-[10px] text-gray-500 mt-1">{shift.startTime} - {shift.endTime}</div>
                                                                </div>
                                                            );
                                                            bgColor = `${shift.color}20`; // Add alpha transparency
                                                        }
                                                    } else {
                                                        content = <div className="font-bold text-xs">{statusLabels[entry.status]}</div>;
                                                        if (entry.status === 'OFF') bgColor = '#f3f4f6';
                                                        if (entry.status === 'VACATION') bgColor = '#dbeafe';
                                                        if (entry.status === 'SICK') bgColor = '#fef9c3';
                                                    }
                                                }

                                                return (
                                                    <td
                                                        key={dateString}
                                                        style={{ ...cellStyle, backgroundColor: bgColor }}
                                                        className="text-center align-top"
                                                    >
                                                        {content}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ ...cellStyle, backgroundColor: '#f9fafb' }} className="text-center text-sm font-semibold text-gray-800">
                                                {staffWeeklyHours[person.id].toFixed(1)}
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
        </div>
    );
};

export default PrintPage;
